/**
 * The view: an orientation-preserving isometry V from (anchored) world coordinates to the disk
 * on screen, and everything that moves it — dragging with inertia, the horocyclic drift,
 * keyboard travel, recentring animations.
 *
 * Anchoring: travelling far makes V's matrix entries grow like e^{d/2} and the world point at
 * the centre creeps toward the unit circle, where single-precision floats in the shader run out
 * of digits. Because the tiling is invariant under its rotation group G⁺, we keep the centre's
 * world point inside the domain W by replacing V with V∘g⁻¹ (g ∈ G⁺) whenever it leaves. Colour
 * classes are permuted by g, so ρ(g) is accumulated and undone in the shader, and measurement
 * points are carried along by g.
 */
import { composePerm, identityPerm, wordPermutation } from '../geometry/colouring';
import type { Complex } from '../geometry/complex';
import {
  type Generator,
  type Isometry,
  IDENTITY,
  addGenerators,
  apply,
  compose,
  expGenerator,
  generatorNorm,
  inverse,
  logIsometry,
  normalize,
  parabolicGenerator,
  rotationGenerator,
  scaleGenerator,
  translateBetween,
  translationGenerator,
} from '../geometry/mobius';
import { type TriangleGroup, fold, wordToIsometry } from '../geometry/triangle-group';
import { distanceFromOrigin } from '../geometry/hyperbolic';

interface Sample {
  t: number;
  g: Isometry;
}

interface Animation {
  X: Generator;
  duration: number;
  elapsed: number;
  /** Fraction of X already applied. */
  applied: number;
}

const DRIFT_SPEED = 0.085;
const DRIFT_PHI = 2.35;
const INERTIA_TAU = 0.36;
const KEY_SPEED = 1.15;
const KEY_TURN = 1.25;

export class ViewController {
  view: Isometry = IDENTITY;
  /** Accumulated anchoring transform (original world → anchored world). */
  anchor: Isometry = IDENTITY;
  /** ρ(anchor) for the current colouring. */
  anchorPerm: number[] = identityPerm(8);
  drift = false;
  reducedMotion = false;
  /** Screen-direction → disk-direction rotation at the centre (for keyboard travel). */
  keyFrame = 0;

  private inertia: Generator | null = null;
  private samples: Sample[] = [];
  private anim: Animation | null = null;
  private keys = new Set<string>();
  private keyRamp = 0;

  /** Called with g whenever the world is re-anchored (world points must be mapped by g). */
  onReanchor: (g: Isometry) => void = () => {};

  get animating(): boolean {
    return this.drift || this.inertia !== null || this.anim !== null || this.keys.size > 0;
  }

  /** The world point at the centre of the view. */
  centreWorld(): Complex {
    return apply(inverse(this.view), { re: 0, im: 0 });
  }

  /** Hyperbolic distance of the view centre from the starting point. */
  distanceFromStart(): number {
    const p = apply(inverse(this.anchor), this.centreWorld());
    return distanceFromOrigin(Math.min(Math.hypot(p.re, p.im), 1 - 1e-16));
  }

  stop(): void {
    this.inertia = null;
    this.anim = null;
    this.samples = [];
  }

  private applyLeft(g: Isometry): void {
    this.view = normalize(compose(g, this.view));
  }

  /** Drag: move the disk point a to b (screen-space disk coordinates). */
  dragTo(a: Complex, b: Complex, t: number): void {
    this.inertia = null;
    this.anim = null;
    const g = translateBetween(a, b);
    this.applyLeft(g);
    this.samples.push({ t, g });
    while (this.samples.length > 0 && t - this.samples[0].t > 110) this.samples.shift();
  }

  /** Two-finger gesture or other direct manipulation. */
  applyGesture(g: Isometry, t: number): void {
    this.inertia = null;
    this.anim = null;
    this.applyLeft(g);
    this.samples.push({ t, g });
    while (this.samples.length > 0 && t - this.samples[0].t > 110) this.samples.shift();
  }

  /** Releases a drag; starts inertia from the recent motion. */
  release(t: number): void {
    const recent = this.samples.filter((s) => t - s.t < 90);
    this.samples = [];
    if (this.reducedMotion || recent.length < 2) return;
    const span = (t - recent[0].t) / 1000;
    if (span <= 0.008) return;
    let total = IDENTITY;
    for (const s of recent) total = compose(s.g, total);
    let X: Generator;
    try {
      X = scaleGenerator(logIsometry(normalize(total)), 1 / span);
    } catch {
      return;
    }
    const n = generatorNorm(X);
    if (n < 0.08) return;
    if (n > 5) X = scaleGenerator(X, 5 / n);
    this.inertia = X;
  }

  rotateBy(theta: number): void {
    this.applyLeft(expGenerator(rotationGenerator(theta)));
  }

  /** Smoothly bring the disk point z to the centre. */
  recentre(z: Complex): void {
    this.inertia = null;
    const g = translateBetween(z, { re: 0, im: 0 });
    if (this.reducedMotion) {
      this.applyLeft(g);
      return;
    }
    this.anim = { X: logIsometry(g), duration: 0.7, elapsed: 0, applied: 0 };
  }

  /** Return to the starting view. Returns the isometry applied to world points. */
  home(): Isometry {
    this.stop();
    const back = inverse(this.anchor);
    this.view = IDENTITY;
    this.anchor = IDENTITY;
    this.anchorPerm = identityPerm(this.anchorPerm.length);
    return back;
  }

  keyDown(code: string): void {
    this.keys.add(code);
    this.inertia = null;
    this.anim = null;
  }

  keyUp(code: string): void {
    this.keys.delete(code);
    if (this.keys.size === 0) this.keyRamp = 0;
  }

  clearKeys(): void {
    this.keys.clear();
    this.keyRamp = 0;
  }

  private keyGenerator(dt: number): Generator | null {
    if (this.keys.size === 0) return null;
    this.keyRamp = Math.min(1, this.keyRamp + dt / 0.25);
    let dx = 0;
    let dy = 0;
    let turn = 0;
    if (this.keys.has('left')) dx -= 1;
    if (this.keys.has('right')) dx += 1;
    if (this.keys.has('up')) dy += 1;
    if (this.keys.has('down')) dy -= 1;
    if (this.keys.has('ccw')) turn += 1;
    if (this.keys.has('cw')) turn -= 1;
    const ease = 0.25 + 0.75 * this.keyRamp;
    let X: Generator = { alpha: 0, beta: { re: 0, im: 0 } };
    if (dx !== 0 || dy !== 0) {
      // travelling in a direction moves the world the opposite way
      const c = Math.cos(this.keyFrame);
      const s = Math.sin(this.keyFrame);
      const dir = { re: -(dx * c - dy * s), im: -(dx * s + dy * c) };
      X = addGenerators(X, translationGenerator(dir, KEY_SPEED * ease));
    }
    if (turn !== 0) X = addGenerators(X, rotationGenerator(turn * KEY_TURN * ease));
    return X;
  }

  /** Advances motion by dt seconds. Returns true if the view changed. */
  tick(dt: number): boolean {
    let changed = false;
    if (this.anim) {
      const a = this.anim;
      a.elapsed += dt;
      const u = Math.min(1, a.elapsed / a.duration);
      const eased = u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2;
      const step = eased - a.applied;
      a.applied = eased;
      this.applyLeft(expGenerator(a.X, step));
      if (u >= 1) this.anim = null;
      changed = true;
    }
    if (this.inertia) {
      this.applyLeft(expGenerator(this.inertia, dt));
      this.inertia = scaleGenerator(this.inertia, Math.exp(-dt / INERTIA_TAU));
      if (generatorNorm(this.inertia) < 0.03) this.inertia = null;
      changed = true;
    }
    const K = this.keyGenerator(dt);
    if (K) {
      this.applyLeft(expGenerator(K, dt));
      changed = true;
    }
    if (this.drift && !this.reducedMotion) {
      this.applyLeft(expGenerator(parabolicGenerator(DRIFT_PHI, DRIFT_SPEED), dt));
      changed = true;
    }
    return changed;
  }

  /**
   * Keeps the centre's world point inside W. `pairs` is the colouring's ρ(m_a m_b) table (or
   * null). Returns true if the anchor moved.
   */
  reanchor(tg: TriangleGroup, pairs: number[][] | null): boolean {
    const c = this.centreWorld();
    const res = fold(tg, c, { rotation: true, recordWord: true, maxIterations: 4000 });
    if (res.word.length === 0) return false;
    const g = wordToIsometry(tg, res.word);
    this.view = normalize(compose(this.view, inverse(g)));
    this.anchor = normalize(compose(g, this.anchor));
    if (pairs) this.anchorPerm = composePerm(wordPermutation(res.word, pairs), this.anchorPerm);
    this.onReanchor(g);
    return true;
  }

  /** Forget the anchoring (after the group changes, the old group's elements mean nothing). */
  resetAnchor(k: number): void {
    this.anchor = IDENTITY;
    this.anchorPerm = identityPerm(k);
  }
}
